/**
 * The walking row (lane B's tree analysis, Oct 10: at a person scale with normal stands, the action bots walk straight
 * into trunks and hop — 1.61 stuck hops a unit-minute against 0.11 with no trees): a look-ahead steer. A bot walking
 * somewhere looks a few person lengths ahead along its way; when something it would walk into lies across that way, it
 * aims past it on the clear side — at the tangent of the thing widened by its own body and a margin — instead of
 * bumping it. It keeps the side it chose while it goes round (no dithering), and it never steers around a shape it is
 * already inside (lane F's rule for segments: a shape you start inside doesn't stop you; the walk pushes it out).
 *
 * What it avoids is a list of shapes of more than one kind, so another kind of thing to keep clear of slots in without
 * a rewrite: `disc` — a trunk, a rock, a structure's foot (read today from the walk obstacles; from lane F's footprints
 * once they are the walk's); `lane` — a band along a segment (66.6, later: a teammate's line of fire).
 *
 * And ground (`steerOffSlopes`, the last stuck hops: at a person scale, the bots that still hopped were in shell craters —
 * a planet-family crater is a pit, its walls far steeper than a person climbs): it looks along its way at the ground,
 * and goes round a stretch too steep to walk up, and never walks down a stretch it could not walk back up — unless where
 * it is going lies below it.
 *
 * Pure geometry on the tangent plane at the walker (arcs short against the planet's radius). Deterministic.
 */
import type { Vec3 } from '../types';
import { vecAdd, vecCross, vecDot, vecLength, vecNormalize, vecScale, vecSub } from '../math';
import { dacos, dcos, dsin } from './dmath';

export type SteerAvoid =
  /** Round a point (a direction from the planet's centre), `radius` u wide. */
  | { kind: 'disc'; dir: Vec3; radius: number }
  /** Clear of a band `radius` u either side of the segment `from` → `to` (directions from the planet's centre). */
  | { kind: 'lane'; from: Vec3; to: Vec3; radius: number };

export interface SteerOptions {
  /** The planet's radius (u). */
  planetRadius: number;
  /** The walker's own half width (u): it passes a shape by this plus `margin`. */
  bodyRadius: number;
  /** How far ahead along its way it looks (u). */
  lookAhead: number;
  /** Clearance beyond touching (u). */
  margin: number;
  /** The side it went round by last (+1 to its left, −1 to its right), kept while something is still in the way. */
  side?: number | null;
  /**
   * 62.43, the local steer (behind `GameConfig.localSteer`; lane C's parked 68.16 rule folded in): where the walk is going.
   * A shape whose clearance holds the goal on the walker's side of it is not gone round — the walk goes to the goal
   * beside it (a cover spot behind a rock, a point by a crate). Absent: every shape in the way is gone round (today).
   */
  goal?: Vec3 | null;
  /**
   * 62.43, the local steer: a walker touching a shape (its body at the shape's edge — a crate or a beacon on a point)
   * slides along it instead of counting as inside it; only a real overlap (half a body in) is left to the walk to push
   * out. Absent: touching counts as inside (today).
   */
  slideAtContact?: boolean;
}

/**
 * 62.65 (d): the steer's geometry in scalars — the same operations in the helpers' order (`local` = normalize, the dot with
 * the walker's normal, the tangent part scaled by the radius; `nearestOnSegment`; the lengths and lateral distances),
 * without a vector object per step: the walk steered every bot every tick and `steerAround` + `reachableGoal` were the
 * page's third and fourth allocation sites at the owner's settings (325 MB in 30 s). Bit for bit the helpers' numbers
 * (tests/steerBuffers.test.ts compares against the vector form over random inputs). Only the move returned is allocated.
 */
export function steerAround(n: Vec3, move: Vec3, avoid: readonly SteerAvoid[], o: SteerOptions, count = avoid.length): { move: Vec3; side: number | null } {   // (62.57: `count` — the first entries of a reused buffer)
  const push = Math.sqrt(move.x * move.x + move.y * move.y + move.z * move.z);
  if (push < 1e-9 || count === 0) return { move, side: null };
  const ip = 1 / push;
  let dx = move.x * ip, dy = move.y * ip, dz = move.z * ip;
  const nx = n.x, ny = n.y, nz = n.z, R = o.planetRadius;
  const inside = (o.slideAtContact ? 0.5 : 1) * o.bodyRadius;
  // the goal, local to the walker (once: the same for every shape)
  let gx = 0, gy = 0, gz = 0;
  const hasGoal = !!o.goal;
  if (o.goal) {
    const g = o.goal, gl = Math.sqrt(g.x * g.x + g.y * g.y + g.z * g.z);
    if (gl < 1e-10) { gx = 0; gy = 0; gz = 0; }
    else { const ig = 1 / gl, ux = g.x * ig, uy = g.y * ig, uz = g.z * ig, d = ux * nx + uy * ny + uz * nz; gx = (ux - nx * d) * R; gy = (uy - ny * d) * R; gz = (uz - nz * d) * R; }
  }
  let side: number | null = null;
  for (let pass = 0; pass < 3; pass++) {
    let blocked = false, bx = 0, by = 0, bz = 0, bReach = 0, bAlong = 0;
    for (let ai = 0; ai < count; ai++) {
      const a = avoid[ai];
      // the shape's nearest point to the walker, and how wide it stands
      let cx: number, cy: number, cz: number;
      if (a.kind === 'disc') {
        const d = a.dir, l = Math.sqrt(d.x * d.x + d.y * d.y + d.z * d.z);
        if (l < 1e-10) { cx = 0; cy = 0; cz = 0; }
        else { const il = 1 / l, ux = d.x * il, uy = d.y * il, uz = d.z * il, dd = ux * nx + uy * ny + uz * nz; cx = (ux - nx * dd) * R; cy = (uy - ny * dd) * R; cz = (uz - nz * dd) * R; }
      } else {
        const f = a.from, fl = Math.sqrt(f.x * f.x + f.y * f.y + f.z * f.z);
        let ax: number, ay: number, az: number;
        if (fl < 1e-10) { ax = 0; ay = 0; az = 0; }
        else { const il = 1 / fl, ux = f.x * il, uy = f.y * il, uz = f.z * il, dd = ux * nx + uy * ny + uz * nz; ax = (ux - nx * dd) * R; ay = (uy - ny * dd) * R; az = (uz - nz * dd) * R; }
        const t2 = a.to, tl = Math.sqrt(t2.x * t2.x + t2.y * t2.y + t2.z * t2.z);
        let ex: number, ey: number, ez: number;
        if (tl < 1e-10) { ex = 0; ey = 0; ez = 0; }
        else { const il = 1 / tl, ux = t2.x * il, uy = t2.y * il, uz = t2.z * il, dd = ux * nx + uy * ny + uz * nz; ex = (ux - nx * dd) * R; ey = (uy - ny * dd) * R; ez = (uz - nz * dd) * R; }
        // the nearest point of the segment to the origin
        const abx = ex - ax, aby = ey - ay, abz = ez - az;
        const l2 = abx * abx + aby * aby + abz * abz;
        if (l2 < 1e-18) { cx = ax; cy = ay; cz = az; }
        else { const t = Math.max(0, Math.min(1, -(ax * abx + ay * aby + az * abz) / l2)); cx = ax + abx * t; cy = ay + aby * t; cz = az + abz * t; }
      }
      const reach = a.radius + o.bodyRadius + o.margin;
      const dist = Math.sqrt(cx * cx + cy * cy + cz * cz);
      if (dist < a.radius + inside) continue;   // already inside it: not steered by it (the walk pushes it out)
      if (hasGoal && a.kind === 'disc') {
        // the goal beside it, on this side: walk on to the goal (its clearance holds the goal; the centre is past it)
        const gcx = gx - cx, gcy = gy - cy, gcz = gz - cz;
        if (Math.sqrt(gcx * gcx + gcy * gcy + gcz * gcz) < reach && gcx * cx + gcy * cy + gcz * cz < 0) continue;
      }
      const along = cx * dx + cy * dy + cz * dz;
      if (along <= 0 || along > o.lookAhead + reach) continue;   // behind, or beyond the look
      const lx = cx - dx * along, ly = cy - dy * along, lz = cz - dz * along;
      const lateral = Math.sqrt(lx * lx + ly * ly + lz * lz);
      if (lateral >= reach) continue;   // its way passes clear
      if (!blocked || along < bAlong) { blocked = true; bx = cx; by = cy; bz = cz; bReach = reach; bAlong = along; }
    }
    if (!blocked) break;
    const dist = Math.sqrt(bx * bx + by * by + bz * bz);
    const id = 1 / dist;
    const tx = bx * id, ty = by * id, tz = bz * id;   // toward the shape
    const lx = ny * tz - nz * ty, ly = nz * tx - nx * tz, lz = nx * ty - ny * tx;   // the walker's left as it faces the shape
    // the side: the one kept, else away from the shape's centre (it lies to the left of the way: go right)
    const s: number = side ?? o.side ?? (lx * dx + ly * dy + lz * dz >= 0 ? 1 : -1);
    side = s;
    // the tangent past it: the shape's direction turned by the angle its widened size subtends
    const q = bReach / dist;   // (no `**`: its last bits differ between engines — tests/dmath.test.ts)
    const ang = dacos(Math.max(-1, Math.min(1, Math.sqrt(Math.max(0, 1 - q * q)))));
    const c = dcos(ang), sn = s * dsin(ang);
    const vx = tx * c + lx * sn, vy = ty * c + ly * sn, vz = tz * c + lz * sn;
    const vl = Math.sqrt(vx * vx + vy * vy + vz * vz);
    if (vl < 1e-10) { dx = 0; dy = 0; dz = 0; } else { const iv = 1 / vl; dx = vx * iv; dy = vy * iv; dz = vz * iv; }
  }
  return { move: { x: dx * push, y: dy * push, z: dz * push }, side };
}

/**
 * 62.43, the local steer (behind `GameConfig.localSteer`): a goal the walk can stand at. A goal inside a shape's walk
 * clearance (its footprint and the walker's body, `bodyRadius`) — a capture point a building stands on, a cover spot
 * pressed against a rock — moves out to `standOff` past that clearance, on the walker's side of the shape (the side of
 * the shape's centre the walker is on); the deepest such shape first, re-checked a few times for a cluster. A goal clear
 * of every shape is returned as it was (the same object). Pure: directions from the planet's centre; `shapes` the
 * discs near the goal. (62.65 (d): in scalars, the helpers' operations in their order — see steerAround.)
 */
export function reachableGoal(walker: Vec3, goal: Vec3, shapes: readonly { dir: Vec3; radius: number }[], o: { planetRadius: number; bodyRadius: number; standOff: number }): Vec3 {
  let g = goal;
  for (let pass = 0; pass < 3; pass++) {
    // the goal's direction, once a pass
    const gl = Math.sqrt(g.x * g.x + g.y * g.y + g.z * g.z);
    let ngx = 0, ngy = 0, ngz = 0;
    if (gl >= 1e-10) { const ig = 1 / gl; ngx = g.x * ig; ngy = g.y * ig; ngz = g.z * ig; }
    let worst: { dir: Vec3; need: number; depth: number } | null = null;
    for (const sh of shapes) {
      const need = sh.radius + o.bodyRadius;
      const d = sh.dir, dl = Math.sqrt(d.x * d.x + d.y * d.y + d.z * d.z);
      let nsx = 0, nsy = 0, nsz = 0;
      if (dl >= 1e-10) { const il = 1 / dl; nsx = d.x * il; nsy = d.y * il; nsz = d.z * il; }
      const arc = dacos(Math.max(-1, Math.min(1, ngx * nsx + ngy * nsy + ngz * nsz))) * o.planetRadius;
      if (arc >= need) continue;
      const depth = need - arc;
      if (!worst || depth > worst.depth) worst = { dir: sh.dir, need, depth };
    }
    if (!worst) break;
    // out from the shape's centre toward the walker (its tangent plane at the shape), `need + standOff` along the ground
    const c = vecNormalize(worst.dir);
    const w = vecNormalize(walker);
    let away = vecSub(w, vecScale(c, vecDot(w, c)));
    if (vecLength(away) < 1e-12) { const gg = vecNormalize(g); away = vecSub(gg, vecScale(c, vecDot(gg, c))); }
    if (vecLength(away) < 1e-12) break;
    away = vecNormalize(away);
    const t = (worst.need + o.standOff) / o.planetRadius;
    g = vecNormalize(vecAdd(vecScale(c, dcos(t)), vecScale(away, dsin(t))));
  }
  return g;
}

export interface SlopeOptions {
  /** The planet's radius (u). */
  planetRadius: number;
  /** How far ahead along its way it looks at the ground (u). */
  lookAhead: number;
  /** The steepest rise it walks up (rise over run, the walk's own). */
  maxSlope: number;
  /** The ground's radius where it is going, or null when it is not going anywhere in particular: it walks down steep
   * ground only toward a place lower than the foot of it. */
  destGround: number | null;
  /** The side it turned to last (+1 its left, −1 its right), tried first. */
  side?: number | null;
  /** Decision 134: the share of the pace it keeps scrambling up what it cannot walk. A turn is taken only while it keeps
   * more of its way than the scramble would (cos turn > scramble); else it scrambles. Absent or 0: any turn. */
  scramble?: number;
}

/** Look-ahead samples along a way, and the turns tried off it (30° to 120°, either side). */
const SLOPE_SAMPLES = 3;
const SLOPE_TURNS = [1, 2, 3, 4].map(i => ({ c: dcos((i * Math.PI) / 6), s: dsin((i * Math.PI) / 6) }));

/** Whether the ground along `dir` from `n` is walkable for `lookAhead`: no rise too steep to climb, and no drop too steep
 * to climb back (unless where it is going lies below the foot of it). */
function walkable(n: Vec3, dir: Vec3, groundAt: (d: Vec3) => number, o: SlopeOptions): boolean {
  const step = o.lookAhead / SLOPE_SAMPLES;
  let prev = groundAt(n);
  for (let i = 1; i <= SLOPE_SAMPLES; i++) {
    const h = groundAt(vecNormalize(vecAdd(n, vecScale(dir, (i * step) / o.planetRadius))));
    const rise = h - prev;
    if (rise > step * o.maxSlope) return false;
    if (-rise > step * o.maxSlope && !(o.destGround !== null && o.destGround < h)) return false;
    prev = h;
  }
  return true;
}

/**
 * The move a walker at `n` should make instead of `move` to keep off ground it could not walk (`groundAt`: the ground's
 * radius under a direction, the walk's own): unchanged when its way is walkable; else turned by the least of 30°, 60°,
 * 90°, 120° that is — the side it turned to last first, and with a scramble only the turns that keep more of its way —
 * and, none being, unchanged (it scrambles, or at a pit's floor without one the stuck hop takes over). `side` back is
 * the side it turned to, or null when it did not.
 */
export function steerOffSlopes(n: Vec3, move: Vec3, groundAt: (d: Vec3) => number, o: SlopeOptions): { move: Vec3; side: number | null } {
  const push = vecLength(move);
  if (push < 1e-9) return { move, side: null };
  const dir = vecScale(move, 1 / push);
  if (walkable(n, dir, groundAt, o)) return { move, side: null };
  const left = vecCross(n, dir);
  const first = o.side ?? 1;
  for (const t of SLOPE_TURNS) {
    if (o.scramble && t.c <= o.scramble) break;   // (turns are in order: past here, scrambling keeps more of its way)
    for (const s of [first, -first]) {
      const d = vecAdd(vecScale(dir, t.c), vecScale(left, s * t.s));
      if (walkable(n, d, groundAt, o)) return { move: vecScale(d, push), side: s };
    }
  }
  return { move, side: null };
}
